import * as actions from './actions';
import { EntriesActionTypes } from './types';
import { NotificationActionTypes } from '../notification/types';
import {
  getEntriesResponse,
  toggleLearnerResubmission,
  toggleComponentDocumentResubmission,
  toggleComponentResubmission,
  getComponentResubmissionStatus,
  getAssociatedLearners,
} from '../../service/entries';
import * as componentsService from '../../service/components';
import { displayNotification } from '../notification/actions';
import { RootState } from '..';
import { getSubmittedComponentDocuments } from '../../service/sections';
import {
  ComponentBusinessId,
  ReplacementFilesComponentDocumentsDto,
  ReplacementFilesComponentLearnersDto,
  Resubmitter,
  SortOrderCapitals,
  VersionedComponentBusinessId,
  VersionedLearnerBusinessId,
} from '@cambridgeassessment/lib-ee-commons';

type Dispatchable = EntriesActionTypes | NotificationActionTypes | ((action: Dispatchable) => Promise<void>);
type Dispatch = (action: Dispatchable) => void | Promise<void>;

export const getComponentLearners =
  (componentIndex: number, bid: VersionedComponentBusinessId, learnerNumber?: string | null) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { replacementFiles } = getState();

      if (!replacementFiles.items[componentIndex]) {
        return;
      }
      const { items, nextPageUrl } = replacementFiles.items[componentIndex].learners;

      if (items.length && !nextPageUrl) {
        return;
      }

      void dispatch(actions.setIsLoadingLearners(componentIndex, true));
      const { results, nextUrl } = await getEntriesResponse(
        bid.businessUnitId ?? '',
        bid.seriesId,
        bid.centreId,
        learnerNumber,
        bid.assessmentCode,
        bid.assessmentVersion,
        bid.componentNumber,
        nextPageUrl,
      );

      void dispatch(actions.addComponentLearners(componentIndex, results, nextUrl));
    } catch (error) {
      console.error(error);
      void dispatch(displayNotification('Failed to get Entries data'));
    } finally {
      void dispatch(actions.setIsLoadingLearners(componentIndex, false));
    }
  };

export const syncSelectAllStatus =
  ({ componentBid, componentIndex }: { componentBid: VersionedComponentBusinessId; componentIndex: number }) =>
  async (dispatch: Dispatch): Promise<void> => {
    const { isAllResubmittable, isAllAbsent } = await getComponentResubmissionStatus(componentBid);
    void dispatch(
      actions.componentResubmissionStatusUpdatedSuccess({
        isAllSelected: isAllResubmittable,
        componentIndex,
        isAllAbsent,
      }),
    );
  };

export const setLearnerReplacementStatus =
  (
    entry: ReplacementFilesComponentLearnersDto,
    componentIndex: number,
    entryIndex: number,
    resubmitter?: Resubmitter,
    toggleAssociations = false,
  ) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const availableForResubmission = resubmitter !== undefined;
      void dispatch(actions.setIsSavingResubmissionStatus(true));

      const learnerBid: VersionedLearnerBusinessId = {
        businessUnitId: entry.businessUnit.id ?? '',
        centreId: entry.centre.id,
        seriesId: entry.seriesId,
        assessmentCode: entry.assessment.code,
        assessmentVersion: entry.assessment.version,
        componentNumber: entry.componentNumber,
        learnerNumber: entry.learner.number,
        learnerUniqueId: entry.learner.uniqueId,
      };

      const learnerBids = await toggleLearnerResubmission(learnerBid, resubmitter, toggleAssociations);
      void dispatch(
        actions.updateLearnersResubmissionStatus(entryIndex, componentIndex, availableForResubmission, learnerBids),
      );

      const componentBid: VersionedComponentBusinessId = {
        businessUnitId: entry.businessUnit.id ?? '',
        centreId: entry.centre.id,
        seriesId: entry.seriesId,
        assessmentCode: entry.assessment.code,
        componentNumber: entry.componentNumber,
        assessmentVersion: entry.assessment.version,
      };

      await dispatch(syncSelectAllStatus({ componentBid, componentIndex }) as Dispatchable);
      void dispatch(
        actions.updateLearnersResubmissionStatus(entryIndex, componentIndex, availableForResubmission, learnerBids),
      );
    } catch (error) {
      console.error(error);
      void dispatch(displayNotification('Failed to set replacement file status'));
    } finally {
      void dispatch(actions.setIsSavingResubmissionStatus(false));
    }
  };
export const toggleSelectedLearner =
  (
    entry: ReplacementFilesComponentLearnersDto,
    componentIndex: number,
    entryIndex: number,
    resubmitter?: Resubmitter,
  ) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { items } = getState().replacementFiles;

      const learnerBid: VersionedLearnerBusinessId = {
        businessUnitId: entry.businessUnit.id ?? '',
        centreId: entry.centre.id,
        seriesId: entry.seriesId,
        assessmentCode: entry.assessment.code,
        assessmentVersion: entry.assessment.version,
        componentNumber: entry.componentNumber,
        learnerNumber: entry.learner.number,
        learnerUniqueId: entry.learner.uniqueId,
      };

      void dispatch(actions.setIsSavingResubmissionStatus(true));
      const availableForResubmission = !!resubmitter;
      const associations = availableForResubmission ? await getAssociatedLearners(learnerBid) : [];
      const associationsUniqueIds = associations.map((as) => as.learnerUniqueId);

      const associatedLearnersResubmissionEnabled = items[componentIndex].learners.items.filter(
        (item) => item.resubmission.enabled && associationsUniqueIds.includes(item.learner.uniqueId),
      );

      const areAllAssociationsToggled =
        associatedLearnersResubmissionEnabled.length === associationsUniqueIds.length - 1;
      const hasAssociations = associations.length - 1 > 0;

      if (hasAssociations && !areAllAssociationsToggled) {
        void dispatch(actions.updateAssociatedLearners(associations));
        void dispatch(actions.setIsLinkedCandidateModalOpen(true));
      } else {
        await setLearnerReplacementStatus(entry, componentIndex, entryIndex, resubmitter)(dispatch);
      }
    } catch {
      void dispatch(displayNotification('Failed to get associated learners'));
    } finally {
      void dispatch(actions.setIsSavingResubmissionStatus(false));
    }
  };

export const setComponentDocumentReplacementStatus =
  (componentIndex: number, document: ReplacementFilesComponentDocumentsDto, resubmitter?: Resubmitter) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const availableForResubmission = !!resubmitter;
      void dispatch(actions.setIsSavingResubmissionStatus(true));
      void dispatch(
        actions.updateDocumentsResubmissionStatus(componentIndex, document.section.id, availableForResubmission),
      );

      const sectionBid = {
        businessUnitId: document.businessUnit.id,
        centreId: document.centre.id,
        seriesId: document.seriesId,
        assessmentCode: document.assessment.code,
        componentNumber: document.componentNumber,
        sectionId: document.section.id,
        learnerNumber: undefined,
        learnerUniqueId: undefined,
      };

      const componentBid: VersionedComponentBusinessId = {
        businessUnitId: document.businessUnit.id ?? '',
        centreId: document.centre.id,
        seriesId: document.seriesId,
        assessmentCode: document.assessment.code,
        assessmentVersion: document.assessment.version,
        componentNumber: document.componentNumber,
      };

      await toggleComponentDocumentResubmission(sectionBid, resubmitter);
      await dispatch(syncSelectAllStatus({ componentBid, componentIndex }) as Dispatchable);
      void dispatch(
        actions.updateDocumentsResubmissionStatus(componentIndex, document.section.id, availableForResubmission),
      );
    } catch (error) {
      console.error(error);
      void dispatch(displayNotification('Failed to set replacement file status'));
    } finally {
      void dispatch(actions.setIsSavingResubmissionStatus(false));
    }
  };

export const setComponentReplacementStatus =
  (componentBid: VersionedComponentBusinessId, componentIndex: number, resubmitter?: Resubmitter) =>
  async (dispatch: Dispatch): Promise<void> => {
    void dispatch(actions.setIsSavingResubmissionStatus(true));
    const availableForResubmission = !!resubmitter;
    try {
      await toggleComponentResubmission(componentBid, resubmitter);
      await dispatch(syncSelectAllStatus({ componentBid, componentIndex }) as Dispatchable);
      void dispatch(actions.componentResubmissionSuccess(componentIndex, availableForResubmission));
    } catch (error) {
      void dispatch(displayNotification('Failed to set component replacement file statuses'));
    } finally {
      void dispatch(actions.setIsSavingResubmissionStatus(false));
    }
  };

export const getComponentsWithSubmittedSections =
  ({
    businessUnitId,
    seriesId,
    centreId,
    learnerNumber,
    assessmentCode,
    componentNumber,
  }: {
    businessUnitId: string;
    seriesId: number;
    centreId: string;
    learnerNumber?: string | null;
    assessmentCode?: string | null;
    componentNumber?: string | null;
  }) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      void dispatch(actions.setIsLoadingComponents(true));
      const results = await componentsService.getComponentsWithSubmittedSections(
        businessUnitId,
        seriesId,
        centreId,
        learnerNumber,
        assessmentCode,
        componentNumber,
        'componentNumber',
        SortOrderCapitals.ASC,
      );

      void dispatch(actions.addComponents(results));
    } catch (error) {
      console.error(error);
      void dispatch(displayNotification('Failed to get replacement files data'));
    } finally {
      void dispatch(actions.setIsLoadingComponents(false));
    }
  };

export const loadComponentDocuments =
  (componentIndex: number, componentBid: ComponentBusinessId) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await getSubmittedComponentDocuments({
        ...componentBid,
        sortBy: 'name',
        order: SortOrderCapitals.ASC,
      });
      void dispatch(actions.addComponentDocuments(componentIndex, data));
    } catch (error) {
      console.error(error);
      void dispatch(displayNotification('Failed to get component level documents'));
    }
  };
